import type { NarrativeTokenMap } from './narrativeTemplateEngine';
import { compareToReference, formatComparatorPhrase, isFiniteNumber } from './narrativeFormatting';

export interface TransportMapNarrativeInput {
  locationName: string;
  layerId: string;
  year: number;
  tractCount: number | null;
  medianTractValue: number | null;
  minTractValue: number | null;
  maxTractValue: number | null;
  jurisdictionValue: number;
  referenceValue: number | null;
  referenceLabel: string;
  evCountEstimated?: boolean;
  zeroPortTractCount?: number | null;
}

function fmtNum(variable: string, v: number): string {
  if (variable === 'income') return `$${Math.round(v).toLocaleString()}`;
  if (variable === 'vehicles_per_household') return v.toFixed(2);
  if (variable === 'ev_adoption') return v.toFixed(1);
  if (variable === 'commute_time') return v.toFixed(1);
  if (variable === 'total_chargers') return v.toFixed(1);
  if (variable === 'population_density') return Math.round(v).toLocaleString();
  return v < 10 ? v.toFixed(2) : Math.round(v).toLocaleString();
}

export function buildTransportMapNarrativeTokens(input: TransportMapNarrativeInput): NarrativeTokenMap {
  const med = input.medianTractValue;
  const min = input.minTractValue;
  const max = input.maxTractValue;
  const tractCount = input.tractCount;

  const benchmarkSentence =
    input.layerId === 'ev_adoption' &&
    input.referenceValue != null &&
    isFiniteNumber(input.referenceValue) &&
    isFiniteNumber(med)
      ? ` The jurisdiction mean was ${fmtNum(input.layerId, input.jurisdictionValue)} plug-in vehicles per 100 people, ${formatComparatorPhrase(
          compareToReference(input.jurisdictionValue, input.referenceValue).relation,
          input.referenceLabel.replace(/ mean.*/i, '')
        )} the ${input.referenceLabel.toLowerCase()} of ${fmtNum(input.layerId, input.referenceValue)}.`
      : '';

  const estimatedCountSentence =
    input.layerId === 'total_evs' && input.evCountEstimated
      ? ' Tract counts marked estimated use (EVs per 100 people × tract population) when native fleet count columns are absent.'
      : input.layerId === 'total_evs' && !input.evCountEstimated
        ? ' Tract counts use native plug-in fleet columns when present in the tract layer.'
        : '';

  const zeroPortSentence =
    input.layerId === 'total_chargers' &&
    input.zeroPortTractCount != null &&
    input.zeroPortTractCount > 0
      ? ` ${input.zeroPortTractCount} tract${input.zeroPortTractCount === 1 ? '' : 's'} had zero public EVSE ports in the active snapshot.`
      : '';

  const rangeSentence =
    min != null && max != null && med != null
      ? ` Values ranged from ${fmtNum(input.layerId, min)} to ${fmtNum(input.layerId, max)}.`
      : '';

  const distributionSentence = rangeSentence;

  const cityCountyContextSentence =
    input.layerId === 'income' && med != null
      ? ` The median tract household income in the selected scope was ${fmtNum('income', med)}.`
      : '';

  return {
    locationName: input.locationName,
    tractCount,
    medianIncome: input.layerId === 'income' && med != null ? fmtNum('income', med) : null,
    minIncome: input.layerId === 'income' && min != null ? fmtNum('income', min) : null,
    maxIncome: input.layerId === 'income' && max != null ? fmtNum('income', max) : null,
    cityCountyContextSentence,
    medianEvCount:
      input.layerId === 'total_evs' && med != null ? fmtNum('total_evs', med) : null,
    minEvCount: input.layerId === 'total_evs' && min != null ? fmtNum('total_evs', min) : null,
    maxEvCount: input.layerId === 'total_evs' && max != null ? fmtNum('total_evs', max) : null,
    estimatedCountSentence,
    medianEvsPer100:
      input.layerId === 'ev_adoption' && med != null ? fmtNum('ev_adoption', med) : null,
    minEvsPer100:
      input.layerId === 'ev_adoption' && min != null ? fmtNum('ev_adoption', min) : null,
    maxEvsPer100:
      input.layerId === 'ev_adoption' && max != null ? fmtNum('ev_adoption', max) : null,
    benchmarkSentence,
    medianVehiclesPerHousehold:
      input.layerId === 'vehicles_per_household' && med != null
        ? fmtNum('vehicles_per_household', med)
        : null,
    minVehiclesPerHousehold:
      input.layerId === 'vehicles_per_household' && min != null
        ? fmtNum('vehicles_per_household', min)
        : null,
    maxVehiclesPerHousehold:
      input.layerId === 'vehicles_per_household' && max != null
        ? fmtNum('vehicles_per_household', max)
        : null,
    distributionSentence,
    medianPorts:
      input.layerId === 'total_chargers' && med != null ? fmtNum('total_chargers', med) : null,
    zeroPortSentence,
    rangeSentence,
    medianDensity:
      input.layerId === 'population_density' && med != null
        ? fmtNum('population_density', med)
        : null,
    minDensity:
      input.layerId === 'population_density' && min != null
        ? fmtNum('population_density', min)
        : null,
    maxDensity:
      input.layerId === 'population_density' && max != null
        ? fmtNum('population_density', max)
        : null,
    areaUnit: 'square mile',
    medianMinutes:
      input.layerId === 'commute_time' && med != null ? fmtNum('commute_time', med) : null,
    minMinutes:
      input.layerId === 'commute_time' && min != null ? fmtNum('commute_time', min) : null,
    maxMinutes:
      input.layerId === 'commute_time' && max != null ? fmtNum('commute_time', max) : null,
    reportingYearOrVintage: input.year,
    medianZeroVehiclePct:
      input.layerId === 'zero_vehicle_households' && med != null
        ? med.toFixed(1)
        : null,
    minZeroVehiclePct:
      input.layerId === 'zero_vehicle_households' && min != null ? min.toFixed(1) : null,
    maxZeroVehiclePct:
      input.layerId === 'zero_vehicle_households' && max != null ? max.toFixed(1) : null,
    zeroVehicleContextSentence: '',
  };
}
